import React, { useEffect, useCallback } from 'react';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import {
  NavigationContainer,
  DefaultTheme,
  DarkTheme,
} from '@react-navigation/native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Navigation from './src/navigation';
import { useTheme } from './src/hooks/useTheme';
import { useAuth } from './src/hooks/useAuth';
import linking from './src/navigation/linking';
import { navigationRef } from './src/navigation/NavigationService';
import 'react-native-reanimated';
import { StyleSheet } from 'react-native';
import { useFonts } from 'expo-font';
import { Ionicons } from '@expo/vector-icons';
import * as SplashScreen from 'expo-splash-screen';
import { Dimensions, Platform } from 'react-native';
import { registerServiceWorker } from './src/utils/registerServiceWorker';
import {
  initializeGA,
  initializeGTM,
  initializeMetaPixel,
  trackPageView,
} from './src/utils/analytics';
import CookieBanner from './src/components/shared/CookieBanner';
import { TourProvider } from './src/context/TourContext';
import { markTourCompleted } from './src/utils/tourPersistence';

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: 1,
    },
  },
});

const { width: screenWidth } = Dimensions.get('window');

/** Paths that require authentication - unauthenticated users are redirected to Login */
const PROTECTED_PATH_PREFIXES = ['/dashboard', '/mood-boards'];

/** Admin paths (e.g. /:hash/admin/dashboard) - protected except admin login */
function isProtectedAdminPath(pathname: string): boolean {
  // /admin/login is for admin sign-in - allow unauthenticated access
  if (pathname.includes('/admin/login')) return false;
  // All other /admin paths require auth
  return pathname.includes('/admin');
}

function isProtectedPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '') || '/';
  const isStandardProtected = PROTECTED_PATH_PREFIXES.some(
    (p) => normalized === p || normalized.startsWith(p + '/')
  );
  const isAdminProtected = isProtectedAdminPath(normalized);
  return isStandardProtected || isAdminProtected;
}

// Wrap the app content with the ThemeProvider
const AppContent = () => {
  const { isDarkMode, colors } = useTheme();
  const { isAuthenticated, isLoading, user } = useAuth();

  const handleTourComplete = useCallback(async () => {
    await markTourCompleted(user?.id ?? null);
  }, [user?.id]);

  // Customize navigation theme based on current theme
  const navigationTheme = {
    ...(isDarkMode ? DarkTheme : DefaultTheme),
    colors: {
      ...(isDarkMode ? DarkTheme.colors : DefaultTheme.colors),
      primary: colors.primary,
      background: colors.background,
      card: colors.card,
      text: colors.text,
      border: colors.border,
    },
  };

  // Track page views on navigation state changes
  const handleNavigationStateChange = () => {
    const currentRoute = navigationRef.getCurrentRoute();
    if (currentRoute) {
      const routeName = currentRoute.name;
      const routeParams = currentRoute.params as
        | { boardId?: string; section?: string }
        | undefined;

      // Build a meaningful path for analytics
      let path = `/${routeName}`;
      if (routeParams?.boardId) {
        path += `/${routeParams.boardId}`;
      }
      if (routeParams?.section) {
        path += `/${routeParams.section}`;
      }

      // For web, use the actual URL path for better tracking
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        path = window.location.pathname + window.location.search;
      }

      trackPageView(path, routeName);
    }
  };

  const sectionTitles: Record<string, string> = {
    home: 'Home',
    'mood-boards': 'Moodboard',
    'color-analysis': 'Color Analysis',
    'virtual-drape-test': 'Virtual Drape',
    'color-generator': 'Color Generator',
    'smart-wardrobe': 'Smart Wardrobe',
    settings: 'Settings',
    profile: 'Profile',
  };

  const routeNameToTitle: Record<string, string> = {
    HomeTab: 'Home',
    MoodBoardTab: 'Moodboard',
    ColourAnalysis: 'Color Analysis',
    VirtualDrapeTest: 'Virtual Drape',
    ColorGenerator: 'Color Generator',
    SmartWardrobe: 'Smart Wardrobe',
    SettingsTab: 'Settings',
    ProfileTab: 'Profile',
    ColourAnalysisMobile: 'Color Analysis',
    VirtualDrapeTestMobile: 'Virtual Drape',
    ColorGeneratorMobile: 'Color Generator',
    MoodBoardTabMobile: 'Moodboard',
    ProfileTabMobile: 'Profile',
    SmartWardrobeMobile: 'Smart Wardrobe',
    SettingsTabMobile: 'Settings',
  };

  const documentTitleFormatter = (
    options: Record<string, unknown> | undefined,
    route: { name: string; params?: { section?: string } } | undefined
  ) => {
    // If screen explicitly sets a title via options.title, use it (highest priority)
    if (options?.title != null) {
      return String(options.title);
    }

    const name = route?.name ?? '';

    if (name === 'MainTabs') {
      let section = route?.params?.section;

      if (!section || section === 'undefined' || section === '') {
        section = 'home';
      }

      const pageTitle = sectionTitles[section] ?? 'Home';

      return `ColourSense | ${pageTitle}`;
    }

    // Handle individual tab screens (when accessed directly, not via MainTabs)
    if (routeNameToTitle[name]) {
      return `ColourSense | ${routeNameToTitle[name]}`;
    }

    // Fallback: use route name or default
    return name || 'ColourSense';
  };

  // Redirect unauthenticated users from protected paths to Login or AdminLogin
  const pathname =
    Platform.OS === 'web' && typeof window !== 'undefined'
      ? window.location.pathname
      : '';
  const shouldRedirect =
    !isLoading && !isAuthenticated && isProtectedPath(pathname);
  const initialState = shouldRedirect
    ? (() => {
        // Admin dashboard paths: redirect to AdminLogin with hash so admins can sign in
        const adminMatch = pathname.match(/^\/([^/]+)\/admin/);
        if (adminMatch) {
          return {
            index: 0,
            routes: [
              { name: 'AdminLogin' as const, params: { hash: adminMatch[1] } },
            ],
          };
        }
        return { index: 0, routes: [{ name: 'Login' as const }] };
      })()
    : undefined;

  return (
    <TourProvider onTourComplete={handleTourComplete}>
      <NavigationContainer
        theme={navigationTheme}
        linking={linking}
        ref={navigationRef}
        initialState={initialState}
        onStateChange={handleNavigationStateChange}
        documentTitle={{ formatter: documentTitleFormatter }}
      >
        <Navigation />
        <CookieBanner />
        <StatusBar style={isDarkMode ? 'light' : 'dark'} />
      </NavigationContainer>
    </TourProvider>
  );
};

export default function App() {
  const [fontsLoaded] = useFonts({
    ...Ionicons.font,
  });

  useEffect(() => {
    SplashScreen.preventAutoHideAsync();

    if (Platform.OS === 'web') {
      registerServiceWorker();
      // If Supabase redirects to / with auth hash (e.g. when Site URL lacks /auth/callback),
      // redirect to /auth/callback so AuthCallbackScreen can handle it
      const pathname = window.location.pathname.replace(/\/$/, '') || '/';
      const hash = window.location.hash || '';
      if (
        pathname === '/' &&
        hash &&
        (hash.includes('access_token=') || hash.includes('error='))
      ) {
        window.location.replace(`/auth/callback${hash}`);
      }
    }

    initializeGA();
    initializeGTM();
    initializeMetaPixel();
  }, []);

  const onLayoutRootView = useCallback(async () => {
    if (fontsLoaded) {
      await SplashScreen.hideAsync();
    }
  }, [fontsLoaded]);

  if (!fontsLoaded) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <GestureHandlerRootView
        style={styles.container}
        onLayout={onLayoutRootView}
      >
        <SafeAreaProvider>
          <AppContent />
        </SafeAreaProvider>
      </GestureHandlerRootView>
    </QueryClientProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web'
      ? { width: '100%' }
      : { width: screenWidth - 60 }),
  },
});
